import PageHero from '../components/PageHero';
import { Reveal, FadeIn } from '../components/Reveal';
import { IMAGES } from '../lib/site';
import { Globe2 } from 'lucide-react';

const METHODOLOGY = [
    {
        n: '01',
        title: 'Diagnose',
        desc: 'Working with the C-suite to isolate target market adoption objectives and corporate development goals.',
    },
    {
        n: '02',
        title: 'Design',
        desc: 'Architecting an integrated program across media relations, partnerships, visibility, and lead generation.',
    },
    {
        n: '03',
        title: 'Deploy',
        desc: 'Activating our network — editorial boards, institutions, summit stages, and capital audiences.',
    },
    {
        n: '04',
        title: 'Scale',
        desc: 'Converting early momentum into repeatable, measurable programs that compound stakeholder influence.',
    },
];

const PRINCIPLES = [
    ['Counsel before campaigns', 'Strategy leads. Every activation traces back to a corporate development objective.'],
    ['Network as infrastructure', 'A decade of relationships across media, government, and capital — deployed on demand.'],
    ['Discretion by default', 'High-stakes mandates handled with the confidentiality boards expect.'],
    ['Measurement at the top', 'Programs report against adoption, pipeline, and influence — not vanity metrics.'],
];

const LEADERSHIP = [
    { initials: 'JW', name: 'J. Whitmore', role: 'Managing Partner, Strategy' },
    { initials: 'AR', name: 'A. Reyes', role: 'Partner, Media & Visibility' },
    { initials: 'DK', name: 'D. Kessler', role: 'Partner, Partnerships & Policy' },
];

const HUBS = ['New York', 'Washington, DC', 'London', 'Geneva', 'Singapore'];

export default function About() {
    return (
        <>
            <PageHero
                overline="About the Firm"
                lines={['Built for the', 'decisions that', 'move markets.']}
                sub="CommPro Worldwide is an integrated strategic program management agency and advisory firm. Since 2015 we have helped organizations accelerate growth and strengthen stakeholder influence across borders, sectors, and institutions."
            />

            <section className="py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <FadeIn>
                            <div className="relative">
                                <div className="absolute -inset-3 border border-bronze/25 translate-x-5 translate-y-5 pointer-events-none" />
                                <img
                                    src={IMAGES.conference}
                                    alt="Strategy session in a modern conference room"
                                    className="relative w-full aspect-[4/5] object-cover grayscale-[30%] contrast-110"
                                />
                            </div>
                        </FadeIn>
                    </div>
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <Reveal>
                            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-bronze" />
                                Since 2015
                            </p>
                            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream leading-tight">
                                From boutique counsel to a <span className="text-bronze">global program engine.</span>
                            </h2>
                            <p className="mt-7 text-base sm:text-lg text-fog leading-relaxed">
                                Founded in 2015, CommPro Worldwide began as a strategic communications
                                counsel to growth-stage leadership teams. Over more than a decade, the firm
                                evolved into a full program management engine — designing scalable campaigns
                                that combine media reach, strategic partnerships, and industry engagement.
                            </p>
                            <p className="mt-5 text-base sm:text-lg text-fog leading-relaxed">
                                Today, our mandate is singular: help organizations accelerate growth and
                                strengthen stakeholder influence — whether entering a regulated market,
                                building a public-private coalition, or putting a principal on the world's
                                most consequential stages.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="dark-section py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Methodology
                        </p>
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream">
                            The CommPro <span className="text-bronze">operating rhythm.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
                        {METHODOLOGY.map((m, i) => (
                            <Reveal key={m.n} delay={i * 0.08}>
                                <div data-testid={`methodology-step-${m.n}`} className="bg-ink hover:bg-surface transition-colors duration-500 p-8 h-full min-h-[16rem] flex flex-col">
                                    <span className="font-mono text-xs tracking-[0.3em] text-bronze">{m.n}</span>
                                    <h3 className="mt-auto font-serif text-2xl text-cream">{m.title}</h3>
                                    <p className="mt-3 text-sm text-fog leading-relaxed">{m.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 sm:py-28 border-t border-line">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
                    <div>
                        <Reveal>
                            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-bronze" />
                                Principles
                            </p>
                            <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-cream">
                                How we <span className="text-bronze">hold the standard.</span>
                            </h2>
                        </Reveal>
                        <div className="mt-10 space-y-0 border-t border-line">
                            {PRINCIPLES.map(([t, d], i) => (
                                <Reveal key={t} delay={i * 0.06}>
                                    <div className="py-6 border-b border-line grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <h3 className="font-serif text-xl text-cream">{t}</h3>
                                        <p className="text-sm text-fog leading-relaxed">{d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Reveal>
                            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-bronze" />
                                Leadership
                            </p>
                            <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-cream">
                                Senior counsel, <span className="text-bronze">every engagement.</span>
                            </h2>
                        </Reveal>
                        <div className="mt-10 space-y-4">
                            {LEADERSHIP.map((p, i) => (
                                <Reveal key={p.initials} delay={i * 0.08}>
                                    <div data-testid={`leadership-card-${i}`} className="flex items-center gap-6 border border-line bg-surface/50 p-6 hover:border-bronze/50 transition-colors duration-500">
                                        <span className="shrink-0 w-16 h-16 border border-bronze/40 flex items-center justify-center font-serif text-xl text-bronze">
                                            {p.initials}
                                        </span>
                                        <div>
                                            <p className="font-serif text-xl text-cream">{p.name}</p>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog mt-1">
                                                {p.role}
                                            </p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.2}>
                            <div className="mt-8 border border-line bg-ink2/60 p-6">
                                <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">
                                    <Globe2 size={14} /> Operating Hubs
                                </p>
                                <p className="mt-3 font-serif text-lg text-cream/85 leading-relaxed">
                                    {HUBS.join('  ·  ')}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
}
